import { useEffect, useRef } from 'react';

interface IUseVisibilityTrackingOptions {
  onVisible: () => void;
  threshold?: number;
  rootMargin?: string;
  once?: boolean;
}

export function useVisibilityTracking<T extends Element = HTMLDivElement>({
  onVisible,
  threshold = 0.5,
  rootMargin = '0px',
  once = true,
}: IUseVisibilityTrackingOptions) {
  const ref = useRef<T>(null);
  const hasTriggered = useRef(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && (!once || !hasTriggered.current)) {
          hasTriggered.current = true;
          onVisible();

          if (once) {
            observer.disconnect();
          }
        }
      },
      {
        threshold,
        rootMargin,
      },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [onVisible, threshold, rootMargin, once]);

  return ref;
}
